import { useMemo } from "react";
import { useNavigate } from "@tanstack/react-router";
import { TablePaginationConfig } from "antd5/es/table";
import { FilterValue, SorterResult } from "antd5/es/table/interface";
import { isEqual, startCase } from "lodash";
import { Flex } from "styles/utility-components";

import { AssignedToCell } from "components/table_components/AssignedToCell";
import { BuyerWithLogoCell } from "components/table_components/BuyerWithLogoCell";
import FrameworkLinkCell from "components/table_components/FrameworkLinkCell";
import { TableCell } from "components/table_components/TableCellStyles";
import { CompanyUserCombobox } from "components/ui/form_components/CompanyUserCombobox";
import { LeadStageSelect } from "components/ui/form_components/StageSelect/LeadStageSelect";
import toast from "components/ui/toast/Toast";
import { appRoutes } from "lib/appRoutes";
import { EllipsisTooltipRouterLink } from "lib/core_components/EllipsisTooltip";
import { EventNames, useTracking } from "lib/tracking";
import { Table } from "../../../lib/core_components/Table";
import { ColumnType, commonTableColumns } from "../../../lib/core_components/Table/ColumnTypes";
import { tableRowSelection } from "../../../lib/core_components/Table/Table";
import { LeadFromSearch } from "../../../lib/hooks/api/leads/useLeads";
import { useUpdateLeads } from "../../../lib/hooks/api/leads/useUpdateLeads";
import { LeadSortBy, LeadSortOrder } from "../../../lib/types/graphQLEnums";
import { Currency } from "../../app_layout/Typography";
import { DateCell } from "../../table_components/DateCell";
import { RelativeDateCell } from "../../table_components/RelativeDateCell";
import { SupplierWithLogoCell } from "../../table_components/SupplierWithLogoCell";
import LeadResultsEmptyState from "./LeadResultsEmptyState";
import { getLeadsColumnName, LeadColumns } from "./utils";

export type SortState = {
  field: LeadSortBy;
  order: LeadSortOrder;
};

function getSortOrder(sort: SortState | undefined, key: LeadSortBy): "ascend" | "descend" | null {
  if (!sort || sort.field !== key) {
    return null;
  }
  return sort.order === LeadSortOrder.Asc ? "ascend" : "descend";
}

// --- Inline editing cells ---

function InlineStageCell({ lead }: { lead: LeadFromSearch }) {
  const { mutate } = useUpdateLeads({
    onSuccess: () => toast.success("Stage updated successfully"),
    onError: () => toast.error("Failed to update stage"),
  });

  return (
    <LeadStageSelect
      value={lead.stage.id}
      truncate
      onValueChange={(stageId) =>
        mutate({ updateLeadsRequest: { ids: [lead.id], updateData: { stageId } } })
      }
    />
  );
}

function InlineAssigneeCell({ lead }: { lead: LeadFromSearch }) {
  const { mutate } = useUpdateLeads({
    onSuccess: () => toast.success("Assignee updated successfully"),
    onError: () => toast.error("Failed to update assignee"),
  });

  return (
    <CompanyUserCombobox
      value={lead.assignedTo?.guid}
      truncate
      onChange={(assignedToId) =>
        mutate({ updateLeadsRequest: { ids: [lead.id], updateData: { assignedToId } } })
      }
    />
  );
}

function getLeadColumns({
  sort,
  handleLeadRouteChange,
  columnSettings,
}: {
  sort: SortState | undefined;
  handleLeadRouteChange: (id: string) => void;
  columnSettings: LeadColumns[];
}): ColumnType<LeadFromSearch>[] {
  const name: ColumnType<LeadFromSearch> = {
    key: LeadSortBy.Name,
    title: getLeadsColumnName("name"),
    render: (lead) => (
      <TableCell minWidth={390}>
        <EllipsisTooltipRouterLink
          fullText={lead.name ?? "--"}
          linkText={lead.name ?? "--"}
          linkProps={{
            ...appRoutes.leadDetail(lead.id),
            onClick: (e) => {
              e.preventDefault();
              handleLeadRouteChange(lead.id);
            },
          }}
        />
      </TableCell>
    ),
    width: 390,
    ellipsis: true,
    sorter: true,
    sortOrder: getSortOrder(sort, LeadSortBy.Name),
    sortDirections: ["descend", "ascend"],
  };

  const buyerName: ColumnType<LeadFromSearch> = {
    key: LeadSortBy.BuyerName,
    title: getLeadsColumnName("buyerName"),
    render: (_, lead) => {
      const stotlesBuyers = lead.buyers.map((buyer) => ({ ...buyer, isCustom: false }));
      const customBuyers = lead.customBuyers.map((buyer) => ({ ...buyer, isCustom: true }));
      return <BuyerWithLogoCell buyers={[...stotlesBuyers, ...customBuyers]} />;
    },
    ellipsis: true,
    sorter: true,
    sortOrder: getSortOrder(sort, LeadSortBy.BuyerName),
    sortDirections: ["descend", "ascend"],
  };

  const stageName: ColumnType<LeadFromSearch> = {
    key: LeadSortBy.StageName,
    title: getLeadsColumnName("stageName"),
    render: (lead) => (
      <TableCell minWidth={160}>
        <InlineStageCell lead={lead} />
      </TableCell>
    ),
    sorter: true,
    sortOrder: getSortOrder(sort, LeadSortBy.StageName),
    sortDirections: ["descend", "ascend"],
    width: 160,
  };

  const assignedTo: ColumnType<LeadFromSearch> = {
    key: LeadSortBy.AssignedTo,
    title: getLeadsColumnName("assignedTo"),
    render: (lead) => (
      <TableCell minWidth={180} overflow="hidden">
        <InlineAssigneeCell lead={lead} />
      </TableCell>
    ),
    ellipsis: { showTitle: false },
    sorter: true,
    sortOrder: getSortOrder(sort, LeadSortBy.AssignedTo),
    sortDirections: ["descend", "ascend"],
    width: 180,
  };

  const createdBy: ColumnType<LeadFromSearch> = {
    key: LeadSortBy.CreatedBy,
    title: getLeadsColumnName("createdBy"),
    render: (lead) => <AssignedToCell assignedTo={lead.createdBy} minWidth={180} />,
    sorter: true,
    sortOrder: getSortOrder(sort, LeadSortBy.CreatedBy),
    sortDirections: ["descend", "ascend"],
    width: 180,
  };

  const value: ColumnType<LeadFromSearch> = {
    ...commonTableColumns.valueColumn,
    key: LeadSortBy.Value,
    title: getLeadsColumnName("value"),
    render: (lead) => (
      <Flex justifyContent="end">
        <Currency value={lead.value} longFormat />
      </Flex>
    ),
    sorter: true,
    sortOrder: getSortOrder(sort, LeadSortBy.Value),
    sortDirections: ["descend", "ascend"],
  };

  const createdAt: ColumnType<LeadFromSearch> = {
    key: LeadSortBy.CreatedAt,
    title: getLeadsColumnName("createdAt"),
    render: (lead) => <DateCell date={lead.createdAt} minWidth={180} />,
    sorter: true,
    sortOrder: getSortOrder(sort, LeadSortBy.CreatedAt),
    sortDirections: ["descend", "ascend"],
  };

  const updatedAt: ColumnType<LeadFromSearch> = {
    key: LeadSortBy.UpdatedAt,
    title: getLeadsColumnName("updatedAt"),
    render: (lead) => <RelativeDateCell date={lead.updatedAt} minWidth={180} />,
    sorter: true,
    sortOrder: getSortOrder(sort, LeadSortBy.UpdatedAt),
    sortDirections: ["descend", "ascend"],
  };

  const expiryDate: ColumnType<LeadFromSearch> = {
    key: LeadSortBy.ExpiryDate,
    title: getLeadsColumnName("expiryDate"),
    render: (lead) => <DateCell date={lead.expiryDate} minWidth={180} />,
    sorter: true,
    sortOrder: getSortOrder(sort, LeadSortBy.ExpiryDate),
    sortDirections: ["descend", "ascend"],
  };

  const framework: ColumnType<LeadFromSearch> = {
    key: "framework",
    title: getLeadsColumnName("framework"),
    render: (_, lead) => <FrameworkLinkCell frameworks={lead.frameworks} />,
  };

  const suppliers: ColumnType<LeadFromSearch> = {
    key: "suppliers",
    title: getLeadsColumnName("suppliers"),

    render: (_, lead) => (
      <SupplierWithLogoCell
        suppliers={lead.suppliers.map((s) => ({
          id: s.id,
          name: s.name,
          logoUrl: s.logoUrl,
        }))}
        recordName={lead.name}
      />
    ),
    ellipsis: true,
  };

  const columns = {
    name,
    buyerName,
    stageName,
    assignedTo,
    createdBy,
    value,
    createdAt,
    updatedAt,
    suppliers,
    expiryDate,
    framework,
  };

  return columnSettings.map((c) => columns[c]);
}

type Props = {
  leads: LeadFromSearch[] | undefined;
  columnSettings: LeadColumns[];
  isLoading: boolean;
  isError: boolean;
  onSelectionChange?: (selectedIds: string[]) => void;
  sort?: SortState;
  onSortChange?: (sort: SortState) => void;
  selectedRowKeys: string[];
  onClearFilters: () => void;
  pagination: TablePaginationConfig;
};

export function LeadsTable({
  leads,
  columnSettings,
  isLoading,
  isError,
  onSelectionChange,
  sort,
  onSortChange,
  selectedRowKeys,
  onClearFilters,
  pagination,
}: Props) {
  const navigate = useNavigate();
  const { logEvent } = useTracking();

  const handleSelectionChange = (selectedKeys: string[]): void => {
    onSelectionChange?.(selectedKeys);
  };

  const columns = useMemo(() => {
    const handleLeadRouteChange = (id: string) => {
      void navigate({ to: `/leads/${id}` });
    };

    return getLeadColumns({
      sort,
      handleLeadRouteChange,
      columnSettings,
    });
  }, [sort, navigate, columnSettings]);

  const handleTableChange = (
    _: TablePaginationConfig,
    __: Record<string, FilterValue | null>,
    sorter: SorterResult<LeadFromSearch> | SorterResult<LeadFromSearch>[],
  ): void => {
    const currentSorter = Array.isArray(sorter) ? sorter[0] : sorter;

    // onChange also fires for pagination and filter events where columnKey is absent.
    // Only handle genuine column-sort interactions.
    const field = currentSorter.columnKey as LeadSortBy;
    if (!field) {
      return;
    }

    // Ant Design always fires an implicit "clear" step (order: undefined) after cycling
    // through all sortDirections, even when null is not in the list. Since we always
    // have an active sort, treat this as cycling back to "descend" for that column.
    const order = currentSorter.order ?? "descend";

    const newSortState: SortState = {
      order: order === "descend" ? LeadSortOrder.Desc : LeadSortOrder.Asc,
      field,
    };

    if (!isEqual(sort, newSortState)) {
      onSortChange?.(newSortState);
      logEvent(EventNames.tableSorted, {
        Context: "Column action",
        "Sorted by": startCase(newSortState.field),
        "Sort order": newSortState.order,
      });
    }
  };

  return (
    <Table
      ariaLabel="Leads table"
      dataSource={leads}
      loading={isLoading}
      columns={columns}
      isError={isError}
      scroll={{ x: "100%" }}
      rowKey="id"
      rowSelection={tableRowSelection(selectedRowKeys, handleSelectionChange, leads ?? [], "id")}
      onChange={handleTableChange}
      locale={{
        emptyText: (
          <Flex justifyContent="center" alignItems="center">
            <LeadResultsEmptyState onClearFilters={onClearFilters} />
          </Flex>
        ),
      }}
      pagination={pagination}
    />
  );
}
